import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { ChevronDown, Globe } from "lucide-react";
import { SocialLinks } from "@/components/SocialLinks";
import { COUNTRIES, countryFlag } from "@/lib/countries";
import { COMPANY } from "@/lib/legal";
import { useLang } from "@/context/LanguageContext";

const SECTIONS = [
  { title: "hc.company", links: ["hc.aboutFindo", "hc.careers", { label: "hc.pressKit", to: "/presse" }, "Blog"] },
  { title: "hc.ourProducts", links: ["hc.findoForEstablishments", "hc.partnerDirectory", "hc.declarationTracking", "hc.shipping"] },
  { title: "hc.termsAndPolicies", links: [
    { label: "hc.termsAndConditions", to: "/cgu" },
    { label: "hc.privacyPolicy", to: "/confidentialite" },
    { label: "Cookies", to: "/cookies" },
    { label: "hc.legalNotice", to: "/mentions-legales" },
  ] },
  { title: "hc.assistance", links: ["hc.helpCenter", { label: "hc.contactUs", to: "/contact" }, "hc.reportAProblem"] },
];

export default function SiteFooter() {
  const { t } = useLang();
  const [open, setOpen] = useState(null);
  const [country, setCountry] = useState("FR");

  return (
    <footer className="bg-[#0E0E12] text-white mt-24">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-14">
        {/* Top: logo + socials */}
        <div className="flex flex-wrap items-center justify-between gap-6 pb-8">
          <Link to="/"><Logo size={36} withText textClass="text-2xl text-white" /></Link>
          <div className="flex items-center gap-3">
            <SocialLinks />
          </div>
        </div>

        {/* Country selector + Google */}
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl pb-10">
          <div className="relative">
            <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
            <select
              value={country} onChange={(e) => setCountry(e.target.value)}
              className="w-full appearance-none rounded-2xl bg-transparent border border-white/25 py-3.5 pl-11 pr-10 text-sm font-semibold focus:border-white outline-none"
              data-testid="footer-country">
              {COUNTRIES.map((c) => <option key={c.code} value={c.code} className="text-black">{c.flag}  {c.name}</option>)}
            </select>
            <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" />
          </div>
          <button className="flex items-center justify-center gap-2.5 rounded-2xl bg-white text-[#0E0E12] py-3.5 text-sm font-bold hover:bg-white/90 transition-colors" data-testid="footer-google">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29.1 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.9 29.1 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 34 26.8 35 24 35c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C9.5 38.6 16.2 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.6 34.9 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
            {t("hc.addOnGoogle")}
          </button>
        </div>

        {/* Accordion sections */}
        <div className="border-t border-white/12">
          {SECTIONS.map((s, i) => (
            <div key={s.title} className="border-b border-white/12">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left font-display text-xl font-medium hover:text-white/80 transition-colors"
                data-testid={`footer-section-${i}`}>
                {t(s.title)}
                <ChevronDown size={22} className={`transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${open === i ? "max-h-96 pb-5" : "max-h-0"}`}>
                <ul className="space-y-3">
                  {s.links.map((l) => {
                    const label = t(typeof l === "string" ? l : l.label);
                    const to = typeof l === "string" ? null : l.to;
                    return (
                      <li key={label}>
                        {to ? (
                          <Link to={to} className="text-white/60 hover:text-white transition-colors text-[15px]" data-testid={`footer-link-${to.slice(1)}`}>{label}</Link>
                        ) : (
                          <a href="#" className="text-white/60 hover:text-white transition-colors text-[15px]">{label}</a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <p className="text-sm text-white/40">© {new Date().getFullYear()} findloop — {t("hc.lostFoundPlatform")} {countryFlag(country)}</p>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("findloop:open-cookies"))}
            className="text-sm text-white/50 hover:text-white underline transition-colors"
            data-testid="footer-manage-cookies">
            {t("hc.manageCookies")}
          </button>
        </div>
        <p className="pt-3 text-xs text-white/35" data-testid="footer-legal-identity">
          {COMPANY.legalName} — {COMPANY.addressShort} — SIRET {COMPANY.siret} — {COMPANY.rcs}
        </p>
      </div>
    </footer>
  );
}
